import React from "react";
import { Btn } from "./components";
import { T } from "./theme";

// ─────────────────────────────────────────────────────────────────────────
// DesktopShell — top chrome, 60/40 split body, sticky footer.
// Mirrors the design system's desktop-flow-v2 layout.
// ─────────────────────────────────────────────────────────────────────────

export type DesktopStep = {
  id: string;
  label: string;
};

export type DesktopShellProps = {
  steps: DesktopStep[];
  currentStepIndex: number; // 0-based
  /** When provided, the step rail is interactive. Returns true if the step is reachable. */
  onStepClick?: (index: number) => void;
  isStepReachable?: (index: number) => boolean;
  eyebrow?: string;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  form: React.ReactNode;
  preview?: React.ReactNode;
  hidePreview?: boolean;
  onBack: (() => void) | null;
  backLabel?: string;
  onPrimary?: () => void;
  primaryLabel?: string;
  primaryDisabled?: boolean;
  footerHint?: React.ReactNode;
  onExit?: () => void;
  /** Optional content rendered in the top-right header chrome, before the
   * "Step X of N" pill. Used by the parent to surface a "Sign in" link
   * for returning users who landed in onboarding by accident. */
  headerRight?: React.ReactNode;
};

export function DesktopShell(props: DesktopShellProps): React.ReactElement {
  const {
    steps,
    currentStepIndex,
    onStepClick,
    isStepReachable,
    eyebrow,
    title,
    subtitle,
    form,
    preview,
    hidePreview,
    onBack,
    backLabel = "Back",
    onPrimary,
    primaryLabel,
    primaryDisabled,
    footerHint,
    onExit,
    headerRight,
  } = props;

  const showPreview = !!preview && !hidePreview;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: T.bgPage,
        color: T.fg,
        zIndex: 250,
        display: "flex",
        flexDirection: "column",
        fontFamily: "'Inter', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, Arial",
      }}
    >
      {/* Top chrome */}
      <div style={{ borderBottom: `1px solid ${T.hairline}` }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "20px 36px",
            gap: 24,
            maxWidth: 1440,
            margin: "0 auto",
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
            <img
              src="/app-icon-trans.png"
              alt=""
              width={36}
              height={36}
              style={{ display: "block" }}
            />
            <div style={{ fontWeight: 800, letterSpacing: "-0.01em", fontSize: "0.98rem" }}>WeightPlanner Pro</div>
          </div>

          <DesktopStepRail
            steps={steps}
            currentStepIndex={currentStepIndex}
            onStepClick={onStepClick}
            isStepReachable={isStepReachable}
          />

          <div style={{ display: "flex", gap: 14, alignItems: "center", flexShrink: 0 }}>
            {headerRight}
            <span style={{ color: T.muted, fontSize: "0.78rem", fontWeight: 600 }}>
              Step {currentStepIndex + 1} of {steps.length}
            </span>
            {onExit ? (
              <Btn kind="ghost" size="sm" onClick={onExit}>
                Save &amp; exit
              </Btn>
            ) : null}
          </div>
        </div>
      </div>

      {/* Body — bounded max-width container, dark right pane sits inside it so it aligns with the right column */}
      <div style={{ flex: 1, position: "relative", minHeight: 0 }}>
        <div
          style={{
            position: "relative",
            height: "100%",
            display: "grid",
            gridTemplateColumns: showPreview ? "minmax(0, 1.5fr) minmax(0, 1fr)" : "minmax(0, 1fr)",
            columnGap: showPreview ? 48 : 0,
            maxWidth: 1440,
            margin: "0 auto",
            minHeight: 0,
          }}
        >
          {showPreview ? (
            <div
              aria-hidden
              style={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left: "60%",
                right: 0,
                background: "#0F1620",
                borderLeft: `1px solid ${T.hairline}`,
              }}
            />
          ) : null}
          <div
            style={{
              padding: "40px 56px",
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
              minWidth: 0,
              position: "relative",
            }}
          >
            <div style={{ maxWidth: 760, width: "100%" }}>
              {eyebrow ? (
                <div style={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: T.muted, marginBottom: 8 }}>
                  {eyebrow}
                </div>
              ) : null}
              <h1 style={{ fontSize: "2.6rem", fontWeight: 700, letterSpacing: "-0.025em", lineHeight: 1.05, margin: "0 0 10px" }}>
                {title}
              </h1>
              {subtitle ? (
                <p style={{ color: T.fg2, fontSize: "1.0rem", maxWidth: 720, lineHeight: 1.5, margin: 0 }}>{subtitle}</p>
              ) : null}
              <div style={{ marginTop: 28 }}>{form}</div>
            </div>
          </div>

          {showPreview ? (
            <div
              style={{
                padding: "40px 36px",
                display: "flex",
                flexDirection: "column",
                gap: 18,
                minHeight: 0,
                overflow: "auto",
                position: "relative",
              }}
            >
              {preview}
            </div>
          ) : null}
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          borderTop: `1px solid ${T.hairline}`,
          background: T.bgPage,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "16px 36px",
            gap: 16,
            maxWidth: 1440,
            margin: "0 auto",
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          <div style={{ flexShrink: 0 }}>
            {onBack ? (
              <Btn kind="ghost" size="md" onClick={onBack}>
                ← {backLabel}
              </Btn>
            ) : (
              <span />
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            {footerHint ? (
              <span style={{ color: T.muted, fontSize: "0.78rem", fontWeight: 600 }}>{footerHint}</span>
            ) : null}
            {primaryLabel && onPrimary ? (
              <Btn kind="primary" size="lg" onClick={onPrimary} disabled={primaryDisabled}
                iconRight={
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14M13 5l7 7-7 7" />
                  </svg>
                }
              >
                {primaryLabel}
              </Btn>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// DesktopStepRail — the step indicator for the top chrome.
// One gradient dot for the active step, lime fill for done, hairline for todo.
// ─────────────────────────────────────────────────────────────────────────

function DesktopStepRail({
  steps,
  currentStepIndex,
  onStepClick,
  isStepReachable,
}: {
  steps: DesktopStep[];
  currentStepIndex: number;
  onStepClick?: (index: number) => void;
  isStepReachable?: (index: number) => boolean;
}): React.ReactElement {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, flex: 1, justifyContent: "center", overflow: "hidden" }}>
      {steps.map((s, i) => {
        const state = i < currentStepIndex ? "done" : i === currentStepIndex ? "active" : "todo";
        const reachable = onStepClick && (isStepReachable ? isStepReachable(i) : state !== "todo");
        const cellBg =
          state === "active"
            ? T.ctaGrad
            : state === "done"
              ? "rgba(132,204,22,0.18)"
              : "rgba(255,255,255,0.04)";
        const cellColor = state === "active" ? "#0a0e14" : state === "done" ? T.lime : T.muted;
        const cellBorder = state === "todo" ? `1px solid ${T.hairlineStrong}` : "none";
        const labelColor = state === "active" ? T.fg : state === "done" ? T.fg2 : T.muted;
        const labelWeight = state === "active" ? 700 : 600;

        const inner = (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              aria-hidden
              style={{
                width: 24,
                height: 24,
                borderRadius: 999,
                display: "grid",
                placeItems: "center",
                fontWeight: 800,
                fontSize: "0.74rem",
                ...T.tabular,
                background: cellBg,
                color: cellColor,
                border: cellBorder,
                transition: `all ${T.motion}`,
              }}
            >
              {state === "done" ? (
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              ) : (
                i + 1
              )}
            </div>
            <div
              style={{
                fontSize: "0.82rem",
                fontWeight: labelWeight,
                color: labelColor,
                letterSpacing: "-0.005em",
                whiteSpace: "nowrap",
              }}
            >
              {s.label}
            </div>
          </div>
        );

        return (
          <React.Fragment key={s.id}>
            {reachable ? (
              <button
                type="button"
                onClick={() => onStepClick(i)}
                aria-current={state === "active" ? "step" : undefined}
                style={{
                  background: "transparent",
                  border: "none",
                  padding: "4px 6px",
                  borderRadius: 8,
                  cursor: "pointer",
                  color: "inherit",
                  font: "inherit",
                }}
              >
                {inner}
              </button>
            ) : (
              <div style={{ padding: "4px 6px" }}>{inner}</div>
            )}
            {i < steps.length - 1 ? (
              <div
                aria-hidden
                style={{
                  width: 24,
                  height: 1.5,
                  background: state === "done" ? "rgba(132,204,22,0.4)" : T.hairline,
                }}
              />
            ) : null}
          </React.Fragment>
        );
      })}
    </div>
  );
}
